import { ContractExecuteTransaction, AccountId } from '@hiero-ledger/sdk'
import type { HederaProvider } from '@hashgraph/hedera-wallet-connect'
import { transactionToBase64String } from '@hashgraph/hedera-wallet-connect'
import { ethers, type JsonRpcProvider } from 'ethers'
import type { SafeTransaction } from '@safe-global/safe-core-sdk-types'
import type { HederaApprovalStatus, SignerApprovals } from './types'
import { getSafeInterface } from './safe-interface'
import { getSafeThresholdFromContract, checkApprovedHashFromContract } from './contract-queries'
import { txDispatch, TxEvent } from '../tx/txEvents'
import { asError } from '../exceptions/utils'
import { HEDERA_MAINNET } from '@/config/constants'
import { safeCreationDispatch, SafeCreationEvent } from '@/features/counterfactual/services/safeCreationEvents'
import { CF_TX_GROUP_KEY } from '@/features/counterfactual/utils'
import { encodeSafeCreationTx } from '@/components/new-safe/create/logic'
import type { ReplayedSafeProps } from '@/store/slices'
import type { ChainInfo, TransactionDetails } from '@safe-global/safe-gateway-typescript-sdk'
import type { MultiSendCallOnlyContractImplementationType } from '@safe-global/protocol-kit'
import estimates from './estimatedGas'
import { convertAccountIdToEVMAddress } from '@/utils/hedera'

/**
 * Get the CAIP-2 formatted signer account ID
 * Format: hedera:<network>:<shard>.<realm>.<num>
 */
const getSignerAccountId = (accountAddress: string, chainId: string): string => {
  const network = chainId === HEDERA_MAINNET ? 'mainnet' : 'testnet'

  // If already in account ID format (0.0.xxxxx), use it
  if (accountAddress.includes('.')) {
    return `hedera:${network}:${accountAddress}`
  }

  // Convert EVM address to account ID format
  // This is a simplified conversion - in production you may need mirror node lookup
  const accountId = AccountId.fromEvmAddress(0, 0, accountAddress)
  return `hedera:${network}:${accountId.toString()}`
}

/**
 * Check transaction result for errors
 */
const checkTransactionResult = (result: any): void => {
  if (!result) {
    throw new Error('Transaction result is empty')
  }

  // Check for common error indicators
  if (result.status && result.status !== 'SUCCESS') {
    throw new Error(`Transaction failed with status: ${result.status}`)
  }
}

/**
 * NOTE: For Hedera, we use the standard dispatchTxProposal from tx-sender
 * which proposes the transaction without signatures (similar to proposers flow)
 * We don't need a specific function here.
 */

/**
 * Execute approveHash onchain using HashPack via WalletConnect
 * Uses hedera_signAndExecuteTransaction JSON-RPC method
 */
export const dispatchHederaApproveHash = async (
  safeTxHash: string,
  contractId: string,
  provider: HederaProvider,
  signerAccountId: string,
  chainId: string,
  txId: string,
): Promise<string> => {
  const nonce = 0
  const eventParams = { txId, nonce }
  const gasLimit = estimates.approveHash.gasLimit

  try {
    const safeInterface = getSafeInterface()
    const functionData = safeInterface.encodeFunctionData('approveHash', [safeTxHash])

    // Build the transaction
    const transaction = new ContractExecuteTransaction()
      .setContractId(contractId)
      .setGas(gasLimit)
      .setFunctionParameters(ethers.getBytes(functionData))

    // Convert to base64 for WalletConnect
    const transactionBase64 = transactionToBase64String(transaction)

    // Get properly formatted signer account ID
    const formattedSignerAccountId = getSignerAccountId(signerAccountId, chainId)

    // Execute via WalletConnect
    const result = await provider.hedera_signAndExecuteTransaction({
      signerAccountId: formattedSignerAccountId,
      transactionList: transactionBase64,
    })

    // Extract transaction ID from result
    const txHash = result.transactionHash ? `0x${result.transactionHash}` : `hedera-tx-${Date.now()}`

    txDispatch(TxEvent.ONCHAIN_SIGNATURE_SUCCESS, eventParams)

    return txHash
  } catch (error) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(error) })
    throw error
  }
}

/**
 * Check if owner has approved the hash
 * Uses JSON-RPC through ethers read-only provider to query contract state
 */
export const checkHederaApprovalStatus = async (
  provider: JsonRpcProvider,
  safeAddress: string,
  safeTxHash: string,
  ownerAddress: string,
): Promise<boolean> => {
  try {
    return await checkApprovedHashFromContract(provider, safeAddress, ownerAddress, safeTxHash)
  } catch (error) {
    console.error(`Error checking hash approval for owner ${ownerAddress}:`, error)
    return false
  }
}

/**
 * Build signature data for approveHash (v=1) pattern
 * Format: r (32 bytes) = padded address, s (32 bytes) = 0, v (1 byte) = 01
 */
const buildApproveHashSignatureData = (address: string): string => {
  const r = address.slice(2).padStart(64, '0')
  const s = '0'.repeat(64)
  const v = '01'
  return r + s + v
}

/**
 * Check if transaction has sufficient approvals
 * Uses JSON-RPC through ethers read-only provider to query contract state
 * Returns signature data for each approved owner (off-chain ECDSA data or constructed approveHash v=1 data)
 *
 * Handles both signature types:
 * - Off-chain ECDSA signatures: extracted from safeTx.signatures
 * - On-chain approveHash signatures: verified via RPC and constructed as v=1 format
 */
export const checkHederaSufficientApprovals = async (
  provider: JsonRpcProvider,
  safeAddress: string,
  safeTxHash: string,
  safeTx: SafeTransaction,
  ownerAddresses: string[],
): Promise<HederaApprovalStatus> => {
  const threshold = await getSafeThresholdFromContract(provider, safeAddress)

  // Build a map of off-chain ECDSA signatures (lowercase address -> signature data)
  // These are standard ECDSA signatures (r, s, v where v is 27 or 28)
  const offChainSignatures = new Map<string, string>()
  safeTx.signatures.forEach((sig, signer) => {
    // sig.data contains the full ECDSA signature (with 0x prefix)
    // Format: r (32 bytes) + s (32 bytes) + v (1 byte) = 65 bytes total
    const sigData = sig.data.startsWith('0x') ? sig.data.slice(2) : sig.data
    offChainSignatures.set(signer.toLowerCase(), sigData)
  })

  // Initialize approvals map with signature info
  const approvals: Record<string, { approved: boolean; data?: string }> = {}

  // Check all owners: they may have off-chain ECDSA signatures, on-chain approveHash, or both
  // Priority: off-chain ECDSA > on-chain approveHash (ECDSA is more complete)
  const ownersToCheckOnChain: string[] = []

  for (const owner of ownerAddresses) {
    const ownerLower = owner.toLowerCase()
    const offChainSig = offChainSignatures.get(ownerLower)

    if (offChainSig) {
      // Owner has off-chain ECDSA signature - use it (has priority over approveHash)
      // Note: Even if they also called approveHash on-chain, we prefer the ECDSA signature
      approvals[owner] = { approved: true, data: offChainSig }
    } else {
      // Owner doesn't have off-chain signature - check if they have approveHash on-chain
      ownersToCheckOnChain.push(owner)
    }
  }

  // Count off-chain ECDSA approvals
  const offChainApprovalCount = Object.values(approvals).filter((a) => a.approved).length

  // If we already have enough off-chain signatures, we can skip on-chain verification
  // But we still mark remaining owners as not approved for completeness
  if (offChainApprovalCount >= threshold) {
    // Mark remaining owners as not approved
    for (const owner of ownersToCheckOnChain) {
      approvals[owner] = { approved: false }
    }

    return {
      approvalCount: offChainApprovalCount,
      threshold,
      isReady: true,
      approvals,
    }
  }

  // Check on-chain approveHash approvals for owners without off-chain ECDSA signatures
  // These will be constructed as v=1 signatures (pre-validated format)
  const onChainApprovalChecks = await Promise.all(
    ownersToCheckOnChain.map((owner) => checkHederaApprovalStatus(provider, safeAddress, safeTxHash, owner)),
  )

  // Populate approvals for on-chain checked owners
  ownersToCheckOnChain.forEach((owner, i) => {
    const isApproved = onChainApprovalChecks[i]
    approvals[owner] = {
      approved: isApproved,
      // If approved on-chain via approveHash, build the v=1 signature data
      // Format: r = owner address (padded), s = zeros, v = 01
      data: isApproved ? buildApproveHashSignatureData(owner) : undefined,
    }
  })

  // Count total approvals (off-chain ECDSA + on-chain approveHash)
  const onChainApprovalCount = onChainApprovalChecks.filter(Boolean).length
  const totalApprovalCount = offChainApprovalCount + onChainApprovalCount

  return {
    approvalCount: totalApprovalCount,
    threshold,
    isReady: totalApprovalCount >= threshold,
    approvals,
  }
}

/**
 * Concatenate signature data from approvals, sorted by owner address
 * Supports mixed signatures: off-chain (ECDSA) and on-chain (approveHash v=1)
 */
const createSignaturesFromApprovals = (approvals: Record<string, { approved: boolean; data?: string }>): string => {
  // Get approved owners with their signature data
  const approvedSignatures = Object.entries(approvals)
    .filter(([_, info]) => info.approved && info.data)
    .map(([owner, info]) => ({ owner, data: info.data! }))

  // Sort by owner address (lowercase comparison for consistency)
  const sorted = approvedSignatures.sort((a, b) => a.owner.toLowerCase().localeCompare(b.owner.toLowerCase()))

  // Concatenate all signature data
  return sorted.map((s) => s.data).join('')
}

/**
 * Execute Safe transaction with collected signatures
 * Supports mixed signatures: off-chain (ECDSA) and on-chain (approveHash)
 * Uses SafeTransaction from protocol-kit instead of HederaSafeTransaction
 */
export const dispatchHederaSafeTxExecution = async (
  safeTx: SafeTransaction,
  contractId: string,
  provider: HederaProvider,
  signerAccountId: string,
  chainId: string,
  approvals: SignerApprovals,
  txId: string,
): Promise<string> => {
  const nonce = safeTx.data.nonce
  const eventParams = { txId, nonce }
  const gasLimit = estimates.execTransaction.gasLimit

  try {
    const signatures = createSignaturesFromApprovals(approvals)

    const safeInterface = getSafeInterface()
    const functionData = safeInterface.encodeFunctionData('execTransaction', [
      safeTx.data.to,
      safeTx.data.value,
      safeTx.data.data,
      safeTx.data.operation,
      safeTx.data.safeTxGas,
      safeTx.data.baseGas,
      safeTx.data.gasPrice,
      safeTx.data.gasToken,
      safeTx.data.refundReceiver,
      '0x' + signatures,
    ])

    // Build the transaction
    const transaction = new ContractExecuteTransaction()
      .setContractId(contractId)
      .setGas(gasLimit)
      .setFunctionParameters(ethers.getBytes(functionData))

    // Convert to base64 for WalletConnect
    const transactionBase64 = transactionToBase64String(transaction)

    // Get properly formatted signer account ID
    const formattedSignerAccountId = getSignerAccountId(signerAccountId, chainId)

    // Emit EXECUTING before sending the transaction
    txDispatch(TxEvent.EXECUTING, eventParams)

    // Execute via WalletConnect (this is synchronous - waits for confirmation)
    const result = await provider.hedera_signAndExecuteTransaction({
      signerAccountId: formattedSignerAccountId,
      transactionList: transactionBase64,
    })

    const txHash = result.transactionHash ? `0x${result.transactionHash}` : `hedera-tx-${Date.now()}`

    txDispatch(TxEvent.PROCESSING, {
      ...eventParams,
      txHash,
      signerAddress: signerAccountId,
      signerNonce: 0,
      gasLimit,
      txType: 'SafeTx',
    })

    // Verify the transaction succeeded
    checkTransactionResult(result)

    txDispatch(TxEvent.PROCESSED, {
      ...eventParams,
      safeAddress: contractId,
      txHash,
    })

    return txHash
  } catch (error) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(error) })
    throw error
  }
}

/**
 * Deploy a new Safe using HashPack via WalletConnect
 * Calls createProxyWithNonce on the SafeProxyFactory contract
 */
export const dispatchHederaSafeCreation = async (
  safeProps: ReplayedSafeProps,
  contractId: string,
  chain: ChainInfo,
  provider: HederaProvider,
  signerAccountId: string,
  safeAddress: string,
): Promise<string> => {
  const gasLimit = estimates.createSafe.gasLimit

  try {
    // Encode the createProxyWithNonce call
    const encodedCreationTx = encodeSafeCreationTx(safeProps, chain)

    // Build the transaction to the ProxyFactory
    const transaction = new ContractExecuteTransaction()
      .setContractId(contractId)
      .setGas(gasLimit)
      .setFunctionParameters(ethers.getBytes(encodedCreationTx))

    // Convert to base64 for WalletConnect
    const transactionBase64 = transactionToBase64String(transaction)

    // Get properly formatted signer account ID
    const formattedSignerAccountId = getSignerAccountId(signerAccountId, chain.chainId)

    // Execute via WalletConnect (this is synchronous - waits for confirmation)
    const result = await provider.hedera_signAndExecuteTransaction({
      signerAccountId: formattedSignerAccountId,
      transactionList: transactionBase64,
    })

    // this hash is 48 bytes from hedera, we need to trim it to 64 bytes to use as transaction hash on evm
    const txHash = result.transactionHash ? `0x${result.transactionHash.slice(0, 64)}` : `hedera-tx-${Date.now()}`

    safeCreationDispatch(SafeCreationEvent.PROCESSING, {
      groupKey: CF_TX_GROUP_KEY,
      txHash,
      safeAddress,
    })

    // Verify the transaction succeeded
    checkTransactionResult(result)

    txDispatch(TxEvent.PROCESSED, {
      groupKey: CF_TX_GROUP_KEY,
      txHash,
      safeAddress,
    })

    return txHash
  } catch (error) {
    safeCreationDispatch(SafeCreationEvent.FAILED, {
      groupKey: CF_TX_GROUP_KEY,
      error: asError(error),
      safeAddress,
    })
    throw error
  }
}

/**
 * Execute batch transactions using multiSend on Hedera
 */
export const dispatchHederaBatchExecution = async (
  txs: TransactionDetails[],
  multiSendContract: MultiSendCallOnlyContractImplementationType,
  contractId: string,
  multiSendTxData: string,
  provider: HederaProvider,
  signerAccountId: string,
  chainId: string,
  nonce: number,
): Promise<string> => {
  const groupKey = multiSendTxData
  const txIds = txs.map((tx) => tx.txId)
  const gasLimit = estimates.execTransaction.gasLimit

  try {
    // Encode the multiSend function call
    const functionData = multiSendContract.encode('multiSend', [multiSendTxData])

    // Build the transaction
    const transaction = new ContractExecuteTransaction()
      .setContractId(contractId)
      .setGas(gasLimit)
      .setFunctionParameters(ethers.getBytes(functionData))

    // Convert to base64
    const transactionBase64 = transactionToBase64String(transaction)

    // Get properly formatted signer account ID
    const formattedSignerAccountId = getSignerAccountId(signerAccountId, chainId)
    const evmSignerAddress = convertAccountIdToEVMAddress(signerAccountId)

    // Emit EXECUTING before sending the transaction
    txIds.forEach((txId) => {
      txDispatch(TxEvent.EXECUTING, { txId, groupKey, nonce })
    })

    // Execute
    const result = await provider.hedera_signAndExecuteTransaction({
      signerAccountId: formattedSignerAccountId,
      transactionList: transactionBase64,
    })

    const txHash = result.transactionHash ? `0x${result.transactionHash.slice(0, 64)}` : `hedera-tx-${Date.now()}`

    // Verify the transaction succeeded
    checkTransactionResult(result)
    const txTo = await multiSendContract.getAddress()

    // Dispatch processing events for all transactions
    txIds.forEach((txId) => {
      txDispatch(TxEvent.PROCESSING, {
        txId,
        txHash,
        groupKey,
        signerNonce: 0,
        signerAddress: evmSignerAddress?.toString() || '',
        txType: 'Custom',
        data: functionData,
        to: txTo,
        nonce,
      })
    })

    return txHash
  } catch (error) {
    txIds.forEach((txId) => {
      txDispatch(TxEvent.FAILED, { txId, error: asError(error), groupKey, nonce })
    })
    throw error
  }
}
